/**
 * SMS web push token lifecycle (SMS binding).
 *
 * Shared mechanics (Firebase app init, getToken/VAPID mint, payload
 * builder, token slot) live in `@aegix/web-push`; SMS's
 * parameterization stays here:
 *
 *   - SMS's OWN per-env Firebase Web App entry + `app_identifier`
 *     (`./config`, ADR-132 Amendment 4) — absent config fails loud and
 *     skips registration entirely.
 *   - SMS's own device id storage key (`./deviceId`).
 *   - `app_name: "sms"` (backend `fcm_routing` routes it to the aegix
 *     sender).
 *   - Explicit service-worker registration handed to the token mint so the
 *     page owns which SW receives pushes (`public/firebase-messaging-sw.js`).
 *   - No alert-audio path (alert audio is AIM-only) and no Beacon/AIM-kiosk
 *     referral gates (SMS's own kiosk gate lives in the bootstrap
 *     component, on SMS's real KioskContext).
 */
import { isSupported } from 'firebase/messaging';
import {
  buildWebPushRegistrationPayload,
  createTokenStore,
  getWebPushFirebaseApp,
  requestFcmRegistrationToken,
} from '@aegix/web-push';
import {
  getSmsFirebaseConfig,
  getSmsAppIdentifier,
  SMS_VAPID_KEY,
} from './config';
import { getSmsDeviceId } from './deviceId';
import { registerFcmToken, deregisterFcmToken } from './fcmApi';
// Workspace-root version for `app_version`, matching AIM's registration
// payload (the boundary crossing is deliberate).
// eslint-disable-next-line @nx/enforce-module-boundaries
import packageInfo from '../../../../package.json';

/** SMS's product routing value on FCMDeviceToken. */
export const SMS_FCM_APP_NAME = 'sms';

/** Storage key for the last token this browser registered (SMS-owned). */
export const SMS_FCM_TOKEN_STORAGE_KEY = 'aegix_sms_fcm_token';

const SW_URL = '/firebase-messaging-sw.js';

/** Coarse browser + OS extraction (AIM parity) — re-exported for API stability. */
export { getDeviceMetadata } from '@aegix/web-push';

const tokenStore = createTokenStore(SMS_FCM_TOKEN_STORAGE_KEY);

/**
 * Register (or refresh) this browser's SMS push token with the backend.
 *
 * Safe to call repeatedly — login, the 7-day refresh interval, and silent
 * `token_refresh` pushes all land here. Never throws; returns the
 * registered token or `null` when registration was skipped (absent config,
 * unsupported browser, denied permission) or failed.
 */
export async function registerSmsPush(): Promise<string | null> {
  try {
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      !('Notification' in window)
    ) {
      return null;
    }
    if (Notification.permission === 'denied') {
      // Blocked by the user — expected state, not an error.
      console.log('[sms-push] Notifications blocked — skipping registration');
      return null;
    }
    if (!(await isSupported())) {
      console.log('[sms-push] FCM unsupported in this browser — skipping');
      return null;
    }

    const config = getSmsFirebaseConfig();
    if (!config) return null; // Absent config — already failed loud.

    const app = getWebPushFirebaseApp(config);
    const registration = await navigator.serviceWorker.register(SW_URL);
    const currentToken = await requestFcmRegistrationToken({
      app,
      vapidKey: SMS_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!currentToken) {
      console.log('[sms-push] No registration token available (permission?)');
      return null;
    }

    await registerFcmToken(
      buildWebPushRegistrationPayload({
        token: currentToken,
        appName: SMS_FCM_APP_NAME,
        appIdentifier: getSmsAppIdentifier(),
        appVersion: packageInfo.version,
        deviceId: getSmsDeviceId(),
      }),
    );
    // Storage-unavailable set is swallowed — deregister-on-logout just
    // becomes a no-op.
    tokenStore.set(currentToken);
    return currentToken;
  } catch (err) {
    const code = (err as { code?: string })?.code;
    if (code === 'messaging/permission-blocked') {
      console.log('[sms-push] Notifications blocked — expected behavior');
    } else {
      console.warn(
        '[sms-push] Push registration failed:',
        code || (err as Error)?.message || err,
      );
    }
    return null;
  }
}

/**
 * Deregister this browser's SMS push token. Called from the logout
 * composition BEFORE the session is torn down (the call needs the
 * authenticated transport). Best-effort: a failure must never block
 * logout.
 */
export async function deregisterSmsPush(): Promise<void> {
  const token = tokenStore.get();
  if (!token) return;
  try {
    await deregisterFcmToken(token, getSmsDeviceId());
  } catch (err) {
    console.warn(
      '[sms-push] Deregistration failed (continuing logout):',
      (err as Error)?.message || err,
    );
  }
  // Cleared regardless of the call's outcome (SMS's original behavior —
  // the next registration mints a fresh slot either way).
  tokenStore.remove();
}
